/**
 * 分级日志 + 强制脱敏器。
 *
 * ARCH §8.4.6：含 `privateKey / signSeed / encSeed / mnemonic / password / words`
 * 的字段一律替换为 `***`，违反即 Review 打回。
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

/** 敏感字段名（小写匹配，含这些子串的 key 会被脱敏）。 */
const SENSITIVE_KEY_PATTERN =
  /privatekey|private_key|signseed|encseed|peerseed|mnemonic|password|passphrase|words|secret|seed|privkey|signature|sig|token/i;

const REDACTED = '***';

/** 环境日志级别（Vite 注入）。 */
const envLevel = ((): LogLevel => {
  const raw = (import.meta.env?.VITE_LOG_LEVEL as string | undefined) ?? 'info';
  return (['debug', 'info', 'warn', 'error'] as LogLevel[]).includes(raw as LogLevel)
    ? (raw as LogLevel)
    : 'info';
})();

let currentLevel: LogLevel = envLevel;

/** 运行时调整日志级别。 */
export function setLogLevel(level: LogLevel): void {
  currentLevel = level;
}

/** 判断 key 是否敏感。 */
export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERN.test(key);
}

/** 对任意值做深度脱敏（不修改入参）。 */
export function redact(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    // 疑似 base64 私钥/助记词串（长且无空格）不单独脱敏，交由 key 判断；
    // 但对明确形如 12 词助记词的字符串直接脱敏，兜底防止误打。
    return value.trim().split(/\s+/).length === 12 ? REDACTED : value;
  }
  if (typeof value !== 'object') return value;

  if (seen.has(value as object)) return '[circular]';
  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, seen));
  }
  if (value instanceof Uint8Array) {
    return REDACTED;
  }

  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = isSensitiveKey(key) ? REDACTED : redact(val, seen);
  }
  return out;
}

function emit(level: LogLevel, scope: string, args: unknown[]): void {
  if (LEVEL_ORDER[level] < LEVEL_ORDER[currentLevel]) return;
  const payload = args.map((a) => (a instanceof Error ? { name: a.name, message: a.message } : redact(a)));
  const prefix = `[MeshLink][${level.toUpperCase()}][${scope}]`;
  switch (level) {
    case 'debug':
      // eslint-disable-next-line no-console
      console.debug(prefix, ...payload);
      break;
    case 'warn':
      // eslint-disable-next-line no-console
      console.warn(prefix, ...payload);
      break;
    case 'error':
      // eslint-disable-next-line no-console
      console.error(prefix, ...payload);
      break;
    default:
      // eslint-disable-next-line no-console
      console.info(prefix, ...payload);
      break;
  }
}

export interface Logger {
  debug(...args: unknown[]): void;
  info(...args: unknown[]): void;
  warn(...args: unknown[]): void;
  error(...args: unknown[]): void;
}

/** 创建带作用域的 logger。 */
export function createLogger(scope: string): Logger {
  return {
    debug: (...args: unknown[]) => emit('debug', scope, args),
    info: (...args: unknown[]) => emit('info', scope, args),
    warn: (...args: unknown[]) => emit('warn', scope, args),
    error: (...args: unknown[]) => emit('error', scope, args),
  };
}

export const logger = createLogger('app');
